import { Spinner, Text } from '@/ui-kit'
import { useCallback, useMemo, useState } from 'react'
import { useNectarTasks } from '../api'
import type { NectarProject } from '../api/models'
import { NectarProjectStatusView, type TerminalStatus } from './NectarProjectStatusView'
import { NectarTasksAllDone } from './NectarTasksAllDone'
import { TASK_TYPE_COMPONENTS } from './task-types'
import { UnsupportedTaskFallback } from './UnsupportedTaskFallback'

type NectarTaskContentProps = {
  project: NectarProject
}

export const NectarTaskContent = ({ project }: NectarTaskContentProps) => {
  const [submittedIds, setSubmittedIds] = useState<Set<string>>(new Set())
  const isActive = project?.enrollment?.status === 'IN_PROGRESS'
  const { data: taskData, isFetching: isFetchingTasks } = useNectarTasks(isActive ? project.id : undefined)
  const tasks = useMemo(() => taskData?.data ?? [], [taskData?.data])

  const onSubmitted = useCallback((taskId: string) => {
    setSubmittedIds((prev) => new Set(prev).add(taskId))
  }, [])

  const pendingTasks = useMemo(
    () => tasks.filter((t) => t.submissionCount === 0 && !submittedIds.has(t.id)),
    [tasks, submittedIds]
  )
  const totalTasks = tasks.length
  const completedCount = totalTasks - pendingTasks.length
  const currentTask = pendingTasks[0]
  const allDone = !isFetchingTasks && tasks.length > 0 && pendingTasks.length === 0
  const progressPct = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0

  const showProjectStatus =
    project &&
    project.enrollment &&
    project.enrollment.status !== 'IN_PROGRESS' &&
    project.enrollment.status !== 'PENDING_REVIEW'

  const Renderer = currentTask ? (TASK_TYPE_COMPONENTS.get(currentTask.type) ?? null) : null

  if (isFetchingTasks && !tasks.length) {
    return (
      <div className="flex w-full justify-center py-6">
        <Spinner className="mt-8" />
      </div>
    )
  }

  return (
    <>
      {showProjectStatus && (
        <NectarProjectStatusView project={project} status={project.enrollment!.status as TerminalStatus} />
      )}
      {(allDone || project.enrollment?.status === 'PENDING_REVIEW') && (
        <NectarTasksAllDone name={project.name} pointsAmount={project.rewardConfig?.amount} />
      )}

      {!allDone && !showProjectStatus && project.enrollment?.status !== 'PENDING_REVIEW' && (
        <>
          <Text className="text-gray-80">
            Task {completedCount + 1} of {totalTasks}
          </Text>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className="bg-green h-full rounded-full transition-all duration-500 ease-out"
              style={{
                width: `${progressPct}%`,
                boxShadow: progressPct > 0 ? '0 0 8px var(--color-green)' : 'none'
              }}
            />
          </div>

          <div className="mx-auto w-full max-w-[600px]">
            {currentTask && Renderer ? (
              <Renderer key={currentTask.id} task={currentTask} projectId={project.id} onSubmitted={onSubmitted} />
            ) : (
              currentTask && <UnsupportedTaskFallback task={currentTask} />
            )}
          </div>
        </>
      )}
    </>
  )
}
